// Pure command-line parsing for the Terminal shell.
//
// Splits a raw input line into a command, its positional arguments, its
// single-letter flags and an optional output redirect. No imports: this runs
// in the browser bundle and under `node --test` unchanged.
//
// KEEP IN SYNC with ./shell-args.d.mts (hand-written type mirror).

/**
 * @typedef {{ value: string, quoted: boolean, op: null | ">" | ">>" }} Token
 */

/**
 * Character scan producing tokens. Quoted text is marked so that `"-r"` stays
 * a literal argument and `">"` stays a literal `>` rather than a redirect.
 * @param {string} line
 * @returns {{ tokens: Token[], error: string | null }}
 */
function tokenize(line) {
  /** @type {Token[]} */
  const tokens = [];
  let cur = "";
  let curStarted = false;
  let curQuoted = false;
  /** @type {null | '"' | "'"} */
  let quote = null;

  const flush = () => {
    if (!curStarted) return;
    tokens.push({ value: cur, quoted: curQuoted, op: null });
    cur = "";
    curStarted = false;
    curQuoted = false;
  };

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quote) {
      if (ch === quote) {
        quote = null;
      } else {
        cur += ch;
      }
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = /** @type {'"' | "'"} */ (ch);
      curStarted = true;
      curQuoted = true;
      continue;
    }
    if (ch === " " || ch === "\t") {
      flush();
      continue;
    }
    if (ch === ">") {
      // A redirect operator terminates the token it is glued to, so
      // `echo hi>>f` parses the same as `echo hi >> f`.
      flush();
      if (line[i + 1] === ">") {
        tokens.push({ value: ">>", quoted: false, op: ">>" });
        i++;
      } else {
        tokens.push({ value: ">", quoted: false, op: ">" });
      }
      continue;
    }
    cur += ch;
    curStarted = true;
  }

  if (quote) return { tokens, error: "unterminated quote" };
  flush();
  return { tokens, error: null };
}

/**
 * Parse one input line.
 *
 * A redirect with no target is reported as an error rather than dropped —
 * silently writing nowhere is the kind of bug that loses a user's file.
 *
 * @param {string} line
 * @returns {{
 *   cmd: string,
 *   args: string[],
 *   flags: Record<string, boolean>,
 *   redirect: { mode: "write" | "append", target: string } | null,
 *   error: string | null,
 * }}
 */
export function parseCommand(line) {
  const empty = {
    cmd: "",
    args: /** @type {string[]} */ ([]),
    flags: /** @type {Record<string, boolean>} */ ({}),
    redirect: /** @type {{ mode: "write" | "append", target: string } | null} */ (null),
    error: /** @type {string | null} */ (null),
  };
  if (typeof line !== "string") return empty;

  const { tokens, error: tokenError } = tokenize(line);
  if (tokenError) return { ...empty, error: tokenError };
  if (tokens.length === 0) return empty;

  /** @type {string[]} */
  const args = [];
  /** @type {Record<string, boolean>} */
  const flags = {};
  /** @type {{ mode: "write" | "append", target: string } | null} */
  let redirect = null;
  let cmd = "";

  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];

    if (tok.op) {
      if (redirect) return { ...empty, cmd, error: "syntax error: multiple redirects" };
      const target = tokens[i + 1];
      if (!target || target.op) {
        return { ...empty, cmd, error: "syntax error: redirect needs a target file" };
      }
      redirect = { mode: tok.op === ">>" ? "append" : "write", target: target.value };
      i++;
      continue;
    }

    if (!cmd) {
      cmd = tok.value;
      continue;
    }

    // `-rp` sets both r and p. A bare `-` is an argument (e.g. `cd -`).
    if (!tok.quoted && tok.value.length > 1 && tok.value.startsWith("-")) {
      for (const letter of tok.value.slice(1)) flags[letter] = true;
      continue;
    }

    args.push(tok.value);
  }

  return { cmd, args, flags, redirect, error: null };
}
